import Tooltips from "@flick/front_end_patterns/src/js/dna/tooltips";
import { ReactComponent as DollarSignIconSvg } from "assets/icon_dollar_sign.svg";
import React, { useEffect, useMemo } from "react";
import { calculatePercentageChangeDelta } from "utils/calculatePercentageChangeDelta";
import { UNIT_CODE_TYPES } from "utils/constants";
import { prepareComparisonChartData } from "utils/progressComparisonChart/prepareComparisonChartData";

import { renderDeltaMessage } from "../DeltaMessage";
import { LoadingIcon } from "../LoadingIcon";
import { ProgressComparisonChart } from "../ProgressComparisonChart";
import { getPeriodTotalSpend, periodTotalSpend } from "./SpendSection.utils";

type SpendSectionProps = {
  thisPeriodAggregatesData: Aggregates;
  previousPeriodAggregatesData: Aggregates;
  yearAgoAggregatesData: Aggregates;
  isThisPeriodAggregatesFetching: boolean;
  isThisPeriodAggregatesSuccess: boolean;
  isPreviousPeriodAggregatesSuccess: boolean;
  isYearAgoAggregatesSuccess: boolean;
  periodName: string;
};

/**
 * @description Used in the Usage Page
 */
export const SpendSection: React.FC<SpendSectionProps> = ({
  thisPeriodAggregatesData,
  previousPeriodAggregatesData,
  yearAgoAggregatesData,
  isThisPeriodAggregatesFetching,
  isThisPeriodAggregatesSuccess,
  isPreviousPeriodAggregatesSuccess,
  isYearAgoAggregatesSuccess,
  periodName,
}) => {
  const previousPeriodPercentageChange = useMemo(() => {
    return calculatePercentageChangeDelta(
      getPeriodTotalSpend(thisPeriodAggregatesData),
      getPeriodTotalSpend(previousPeriodAggregatesData),
    );
  }, [thisPeriodAggregatesData, previousPeriodAggregatesData]);

  const yearAgoPeriodPercentageChange = useMemo(() => {
    return calculatePercentageChangeDelta(
      getPeriodTotalSpend(thisPeriodAggregatesData),
      getPeriodTotalSpend(yearAgoAggregatesData),
    );
  }, [thisPeriodAggregatesData, yearAgoAggregatesData]);

  useEffect(() => {
    new Tooltips().start();
  }, [isThisPeriodAggregatesFetching]);

  const displayPreviousPeriodDeltaMessage =
    isPreviousPeriodAggregatesSuccess &&
    previousPeriodAggregatesData &&
    isThisPeriodAggregatesSuccess;

  const displayYearAgoPeriodDeltaMessage =
    isYearAgoAggregatesSuccess &&
    yearAgoAggregatesData &&
    isThisPeriodAggregatesSuccess;

  return (
    <section className="usage_details_section">
      <section className="header_section">
        <DollarSignIconSvg data-testid="icon" />
        <h2 className="heading heading_subpage">
          {isThisPeriodAggregatesFetching ? (
            <LoadingIcon />
          ) : (
            <>
              {periodTotalSpend(thisPeriodAggregatesData, periodName)}
              <span
                data-tooltip="The 'Spent' amount is the sum total of the power 'Bought' minus the power 'Sold'."
                className="tooltip_inline"
                data-testid={"tooltip"}
              />
            </>
          )}
        </h2>
        {displayPreviousPeriodDeltaMessage &&
          renderDeltaMessage({
            delta: previousPeriodPercentageChange,
            periodLabel: `last ${periodName}`,
          })}
        {displayYearAgoPeriodDeltaMessage &&
          renderDeltaMessage({
            delta: yearAgoPeriodPercentageChange,
            periodLabel: "last year",
          })}
      </section>
      <ProgressComparisonChart
        data={prepareComparisonChartData({
          thisPeriodData: getPeriodTotalSpend(thisPeriodAggregatesData),
          previousPeriodData: getPeriodTotalSpend(previousPeriodAggregatesData),
          yearAgoData: getPeriodTotalSpend(yearAgoAggregatesData),
          isThisPeriodAggregatesSuccess,
          isPreviousPeriodAggregatesSuccess,
          isYearAgoAggregatesSuccess,
          periodName,
          unitType: UNIT_CODE_TYPES.cost,
        })}
      />
    </section>
  );
};
